import InputLabelField from '@/components/form-control/InputLabelField'
import SelectLabel from '@/components/form-control/SelectLabel'
import {
  DATA_TABLE_PUMPS,
  LIST_ACTIVE,
  LIST_CONTROLLER_SENSOR,
  LIST_PIPE,
  LIST_SITE,
} from '@/constant/common'

import { useConfigUI } from '@/features/config/context'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  Container,
  FormControl,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import * as yup from 'yup'
import useTranslation from '@/hooks/useTranslation'

interface PumpsViewProps {
  id?: string
}

const schema = yup
  .object({
    site: yup.string().required('Site is required'),
    description: yup.string().required('Description is required'),
    controller: yup.string().required('Decoder is required'),
    active: yup.string().required('Active is required'),
    pipe: yup.string().required('Pipe is required'),
    priority: yup.string().required('Priority is required'),
    io: yup.string().required('IO is required'),
    start_delay: yup.string().required('Start Delay is required'),
    stop_delay: yup.string().required('Stop Delay is required'),
    capacity: yup.string().required('Capacity is required'),
  })
  .required()

const PumpsView = ({ id }: PumpsViewProps) => {
  const { t } = useTranslation()
  const { closeSidebar } = useConfigUI()
  const [site, setSite] = useState('')
  const [controller, setController] = useState('')
  const [pipe, setPipe] = useState('')
  const [active, setActive] = useState('')

  const inputRefSiteName = useRef<HTMLInputElement>(null)
  const data = DATA_TABLE_PUMPS.find((item) => item.id === id)

  useEffect(() => {
    if (inputRefSiteName.current) {
      inputRefSiteName.current.focus()
    }
  }, [])

  const handleSite = (event: SelectChangeEvent) => {
    setSite(event.target.value)
  }
  const handleController = (event: SelectChangeEvent) => {
    setController(event.target.value)
  }
  const handlePipe = (event: SelectChangeEvent) => {
    setPipe(event.target.value)
  }
  const handleActive = (event: SelectChangeEvent) => {
    setActive(event.target.value)
  }
  const form = useForm({
    defaultValues: {
      site: '',
      description: '',
      controller: '',
      active: '',
      pipe: '',
      priority: '',
      io: '',
      start_delay: '',
      stop_delay: '',
      capacity: '',
      ...data,
    },
    resolver: yupResolver(schema),
  })

  const handleSave = () => {
    try {
      closeSidebar()
      toast.success(t('addPumpsSuccess'))
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        console.error('error', error)
      }
    }
  }

  return (
    <Container sx={{ maxWidth: '450px' }}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        <Stack sx={{ padding: '34px 18px' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '377px' }}
          >
            <Typography
              sx={{
                fontWeight: '500',
                lineHeight: '14px',
                color: '#000000',
              }}
            >
              {id ? t('EditPump') : t('AddPump')}
            </Typography>
            <Stack direction="row">
              <Button
                color="secondary"
                sx={{ marginRight: '7px', width: '85px' }}
                onClick={() => closeSidebar()}
              >
                {t('commonActionsCancel')}
              </Button>
              <Button color="primary" sx={{ minWidth: '72px' }} type="submit">
                {t('commonActionsSave')}
              </Button>
            </Stack>
          </Stack>

          <Stack>
            <FormControl
              variant="standard"
              sx={{
                minWidth: '377px',
                minHeight: '50px',
                marginTop: '8px',
                backgroundColor: 'background.default',
                borderRadius: '4px',
              }}
            >
              <Stack sx={{ padding: '8px 12px' }}>
                <SelectLabel
                  name="Site"
                  onChange={handleSite}
                  listItem={LIST_SITE}
                  value={site}
                />
              </Stack>
            </FormControl>
          </Stack>

          <Stack>
            <FormControl
              variant="standard"
              sx={{
                minWidth: '377px',
                minHeight: '50px',
                marginTop: '8px',
                backgroundColor: 'background.default',
                borderRadius: '4px',
              }}
            >
              <Stack sx={{ padding: '8px 12px' }}>
                <SelectLabel
                  name="Decoder"
                  onChange={handleController}
                  listItem={LIST_CONTROLLER_SENSOR}
                  value={controller}
                />
              </Stack>
            </FormControl>
          </Stack>

          <Stack>
            <FormControl
              variant="standard"
              sx={{
                minWidth: '377px',
                minHeight: '50px',
                marginTop: '8px',
                backgroundColor: 'background.default',
                borderRadius: '4px',
              }}
            >
              <Stack sx={{ padding: '8px 12px' }}>
                <SelectLabel
                  name="Pipe"
                  onChange={handlePipe}
                  listItem={LIST_PIPE}
                  value={pipe}
                />
              </Stack>
            </FormControl>
          </Stack>

          <InputLabelField
            name="io"
            form={form}
            label="IO"
            placeholder="IO"
            style={{
              height: 50,
              borderRadius: '4px',
              width: { xs: '377px' },
              marginTop: '8px',
            }}
            padding="8px 16px"
          />

          <Stack>
            <FormControl
              variant="standard"
              sx={{
                minWidth: '377px',
                minHeight: '50px',
                marginTop: '8px',
                backgroundColor: 'background.default',
                borderRadius: '4px',
              }}
            >
              <Stack sx={{ padding: '8px 12px' }}>
                <SelectLabel
                  name="Active"
                  onChange={handleActive}
                  listItem={LIST_ACTIVE}
                  value={active}
                />
              </Stack>
            </FormControl>
          </Stack>

          <InputLabelField
            name="start_delay"
            form={form}
            label={t('formsLabelsStartDelay')}
            placeholder={t('formsLabelsStartDelay')}
            style={{
              height: 50,
              borderRadius: '4px',
              width: { xs: '377px' },
              marginTop: '8px',
            }}
            padding="8px 16px"
          />

          <InputLabelField
            name="stop_delay"
            form={form}
            label={t('formsLabelsStopDelay')}
            placeholder={t('formsLabelsStopDelay')}
            style={{
              height: 50,
              borderRadius: '4px',
              width: { xs: '377px' },
              marginTop: '8px',
            }}
            padding="8px 16px"
          />

          <InputLabelField
            name="capacity"
            form={form}
            label={`${t('formsLabelsCapacity')} (l/m)`}
            placeholder={t('formsLabelsCapacity')} 
            style={{
              height: 50,
              borderRadius: '4px',
              width: { xs: '377px' },
              marginTop: '8px',
            }}
            padding="8px 16px"
          />
        </Stack>
      </form>
    </Container>
  )
}

export default PumpsView
